// SPDX-License-Identifier: AGPL-3.0-or-later
import type { Dataset, Channel } from '../types';
import { num, rate, pct, esc, tip, CHANNEL_COLOUR, CHANNEL_LABEL, REMOTENESS_ORDER, REMOTENESS_SHORT } from '../format';
import { gloss } from '../glossary';
import { multiLine } from '../charts';

const CHANNELS: Channel[] = ['branch', 'bankpost', 'other', 'atm'];

export function renderRemoteness(root: HTMLElement, data: Dataset): void {
  const { national } = data;
  const years = national.years;
  const N = years.length;
  const pop = national.remotenessPop;

  // branches per 100k by remoteness over time
  const per100k = (rem: string, ch: Channel, i: number): number => {
    const s = national.byRemoteness[rem];
    return s && pop[rem] ? (s[ch][i] / pop[rem]) * 100000 : 0;
  };
  const lineSeries = REMOTENESS_ORDER.filter((r) => pop[r]).map((r) => ({
    key: r,
    label: REMOTENESS_SHORT[r] ?? r,
    colour: {
      'Major Cities of Australia': '#1e3a5f',
      'Inner Regional Australia': '#3f7ca0',
      'Outer Regional Australia': '#c98a3a',
      'Remote Australia': '#b3623a',
      'Very Remote Australia': '#8f3f2e',
    }[r] ?? '#7a8699',
    values: years.map((_, i) => per100k(r, 'branch', i)),
  }));

  root.innerHTML = `
    <div class="view-head">
      <h2>Remoteness &amp; access</h2>
      <p>Is the bush losing its banks faster than the cities? Measured per 100,000 residents so places of very different size are comparable, using ABS ${gloss('remoteness', 'remoteness areas')}.</p>
    </div>

    <div class="panel">
      <div class="panel-head"><h3>Branches per 100,000 residents</h3><p>Counter-intuitively, remote and regional Australia still has <em>more</em> branches per head than the cities — small towns need a physical presence. But every line is falling, and the question is who can least afford it.</p></div>
      <div class="chart-wrap">${multiLine(years, lineSeries, { w: 860, h: 340, fmt: (v) => v.toFixed(0) })}</div>
    </div>

    <div class="two-col">
      <div class="panel">
        <div class="panel-head"><h3>How much each relies on Bank@Post</h3><p>Share of all staffed counters (branch + Bank@Post + other) that are ${gloss('bankpost', 'Bank@Post')}, ${years[N - 1]}. The further from the city, the more the post office carries.</p></div>
        <div class="barlist">
          ${REMOTENESS_ORDER.map((r) => {
            const s = national.byRemoteness[r];
            if (!s) return '';
            const staffed = s.branch[N - 1] + s.bankpost[N - 1] + s.other[N - 1];
            const share = staffed ? s.bankpost[N - 1] / staffed : 0;
            return `<div class="barrow" style="cursor:default" data-tip="${tip(`${REMOTENESS_SHORT[r]}: ${num(s.bankpost[N - 1])} of ${num(staffed)} staffed counters are Bank@Post`)}">
              <span class="lbl">${esc(REMOTENESS_SHORT[r] ?? r)}</span>
              <span class="track"><span style="width:${(share * 100).toFixed(1)}%;background:var(--bankpost)"></span></span>
              <span class="val">${pct(share, 0)}</span>
            </div>`;
          }).join('')}
        </div>
      </div>

      <div class="panel">
        <div class="panel-head"><h3>Access mix, ${years[N - 1]}</h3><p>Physical points per 100,000 residents by channel and remoteness. Colour is intensity within each row.</p></div>
        <div class="chart-wrap scroll-x">${matrix(national, years, pop)}</div>
      </div>
    </div>
  `;
}

function matrix(national: Dataset['national'], years: number[], pop: Record<string, number>): string {
  const N = years.length;
  const rows = REMOTENESS_ORDER.filter((r) => pop[r]);
  const rowH = 34, labelW = 130, cellW = 118;
  const w = labelW + cellW * CHANNELS.length + 6;
  const h = rows.length * rowH + 26;
  // find max per-100k per channel column for scaling
  const colMax = CHANNELS.map((ch) => Math.max(...rows.map((r) => (national.byRemoteness[r][ch][N - 1] / pop[r]) * 100000), 1));
  let out = '';
  CHANNELS.forEach((ch, ci) => {
    out += `<text x="${labelW + ci * cellW + cellW / 2}" y="16" text-anchor="middle" class="axis-text" style="font-weight:600">${CHANNEL_LABEL[ch]}</text>`;
  });
  rows.forEach((r, ri) => {
    const y = 22 + ri * rowH;
    out += `<text x="${labelW - 6}" y="${y + rowH / 2 + 3}" text-anchor="end" class="axis-text">${esc(REMOTENESS_SHORT[r] ?? r)}</text>`;
    CHANNELS.forEach((ch, ci) => {
      const raw = national.byRemoteness[r][ch][N - 1];
      const val = (raw / pop[r]) * 100000;
      const t = val / colMax[ci];
      const x = labelW + ci * cellW;
      out += `<rect class="matrix-cell" x="${x}" y="${y}" width="${cellW - 3}" height="${rowH - 4}" rx="3" fill="${mix('#f1f4f6', hexFor(ch), t)}" data-tip="${tip(`${REMOTENESS_SHORT[r]} · ${CHANNEL_LABEL[ch]}\n${num(raw)} points · ${rate(val, 1)} per 100k`)}" /><text x="${x + (cellW - 3) / 2}" y="${y + rowH / 2 + 3}" text-anchor="middle" style="font-size:10px;fill:${t > 0.55 ? '#fff' : 'var(--text-secondary)'}">${rate(val, 1)}</text>`;
    });
  });
  return `<svg class="chart" viewBox="0 0 ${w} ${h}" width="${w}" role="img" aria-label="Access per 100k by remoteness and channel">${out}</svg>`;
}

const hexFor = (ch: Channel): string => CHANNEL_COLOUR[ch];
function mix(a: string, b: string, t: number): string {
  const pa = [1, 3, 5].map((i) => parseInt(a.slice(i, i + 2), 16));
  const pb = [1, 3, 5].map((i) => parseInt(b.slice(i, i + 2), 16));
  const c = pa.map((v, i) => Math.round(v + (pb[i] - v) * Math.max(0, Math.min(1, t))));
  return `#${c.map((v) => v.toString(16).padStart(2, '0')).join('')}`;
}
